import { useGeckoPermissions } from 'features/permissions'
import { useCurrentUserScopes } from 'features/permissions/hooks/useCurrentUserScopes'
import {
  canOpenManageAccessAtSite,
  userCanSeeManageAccess,
} from 'features/permissions/utils/policyScopeEvaluation'
import {
  getOrgAdminPermission,
  PERMISSION_CANTILEVER_CORE_ADMIN,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexPermissionsValues,
} from 'types'

export interface SiteSelectorCapabilities {
  canEditOrganizations: boolean
  canEditSites: boolean
  canManageAccess: boolean
}

export type SiteSelectorCapabilityState = SiteSelectorCapabilities & {
  hasLoadedPermissions: boolean
}

export const getSiteSelectorCapabilities = ({
  hasPermissions,
  selectedOrganizationId,
  canOpenManageAccess,
  allowMutations = true,
  useOverlayManageAccess = false,
}: {
  hasPermissions: (permissions: RolodexPermissionsValues[]) => boolean
  selectedOrganizationId: string | undefined
  /** Whether v3 scopes allow Manage Access at the selected site. */
  canOpenManageAccess: boolean
  allowMutations?: boolean
  useOverlayManageAccess?: boolean
}): SiteSelectorCapabilities => {
  const isInternalUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  return {
    canEditOrganizations:
      allowMutations &&
      isInternalUser &&
      hasPermissions([PERMISSION_CANTILEVER_CORE_ADMIN]),
    canEditSites:
      allowMutations &&
      isInternalUser &&
      hasPermissions([
        PERMISSION_CANTILEVER_CORE_ADMIN,
        getOrgAdminPermission(selectedOrganizationId ?? ''),
      ]),
    canManageAccess: userCanSeeManageAccess(
      canOpenManageAccess,
      hasPermissions,
      useOverlayManageAccess
    ),
  }
}

/** Manage Access link is decided per selected site, so a member-only site hides it. */
export const useSiteSelectorCapabilities = (
  selectedOrganizationId: string | undefined,
  selectedSiteId?: string
): SiteSelectorCapabilityState => {
  const { hasPermissions, permissions, accessPreview } = useGeckoPermissions()
  const { scopes, isLoading: isAccessScopeLoading } = useCurrentUserScopes(
    selectedOrganizationId
  )

  return {
    hasLoadedPermissions: permissions !== undefined && !isAccessScopeLoading,
    ...getSiteSelectorCapabilities({
      hasPermissions,
      selectedOrganizationId,
      canOpenManageAccess: canOpenManageAccessAtSite(
        scopes ?? [],
        selectedSiteId,
        selectedOrganizationId
      ),
      allowMutations: !accessPreview,
      useOverlayManageAccess: Boolean(accessPreview),
    }),
  }
}

export const isSiteSelectorUseful = ({
  organizationCount,
  siteCountForSelectedOrganization,
  capabilities,
}: {
  organizationCount: number
  siteCountForSelectedOrganization: number
  capabilities: Pick<
    SiteSelectorCapabilities,
    'canEditOrganizations' | 'canEditSites'
  >
}) =>
  organizationCount > 1 ||
  siteCountForSelectedOrganization > 1 ||
  capabilities.canEditOrganizations ||
  capabilities.canEditSites

export const shouldShowEmbeddedSidebarBrand = ({
  isLoading,
  hasEverLoaded,
  hasLoadedPermissions,
  organizationIds,
  siteIdsForSelectedOrganization,
  selectedOrganizationId,
  selectedSiteId,
  capabilities,
}: {
  isLoading: boolean
  hasEverLoaded: boolean
  hasLoadedPermissions: boolean
  organizationIds: ReadonlyArray<string>
  siteIdsForSelectedOrganization: ReadonlyArray<string>
  selectedOrganizationId: string | undefined
  selectedSiteId: string | undefined
  capabilities: SiteSelectorCapabilities
}) => {
  const hasResolvedSelection = Boolean(
    selectedOrganizationId &&
    selectedSiteId &&
    organizationIds.includes(selectedOrganizationId) &&
    siteIdsForSelectedOrganization.includes(selectedSiteId)
  )

  if (
    isLoading ||
    !hasEverLoaded ||
    !hasLoadedPermissions ||
    !hasResolvedSelection
  )
    return false

  return !isSiteSelectorUseful({
    organizationCount: organizationIds.length,
    siteCountForSelectedOrganization: siteIdsForSelectedOrganization.length,
    capabilities,
  })
}
